import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import { fetchAbout, fetchFeatureHistory, triggerUpdate, type FeatureHistoryEntry } from '../api/about';
import { listWps } from '../api/dev';
import { UpdateDialog } from '../components/admin/UpdateDialogs';
import { usePermission } from '../hooks/usePermission';
import { useSnackbar } from '../context/SnackbarContext';
import frontendPkg from '../../package.json';

// ── Tech stack groups ──────────────────────────────────────────────────────

const STACK_GROUPS = [
  {
    label: 'Backend',
    items: [
      { label: 'Node.js 20',     color: '#8bc34a' },
      { label: 'TypeScript',     color: '#64b5f6' },
      { label: 'Express',        color: '#bdbdbd' },
      { label: 'Socket.io',      color: '#4dd0c4' },
      { label: 'Zod',            color: '#ce93d8' },
      { label: 'Pino',           color: '#bdbdbd' },
      { label: 'MySQL 8',        color: '#ffb74d' },
      { label: 'TanStack Query', color: '#ef9a9a' },
    ],
  },
  {
    label: 'Frontend',
    items: [
      { label: 'React 18',       color: '#81d4fa' },
      { label: 'MUI v5',         color: '#64b5f6' },
      { label: 'Vite',           color: '#ce93d8' },
      { label: 'TypeScript',     color: '#64b5f6' },
      { label: 'TanStack Query', color: '#ef9a9a' },
      { label: 'Socket.io',      color: '#4dd0c4' },
    ],
  },
  {
    label: 'Infrastructure',
    items: [
      { label: 'nginx',    color: '#a5d6a7' },
      { label: 'systemd',  color: '#bdbdbd' },
      { label: 'UFW',      color: '#ef9a9a' },
      { label: 'PAM',      color: '#bdbdbd' },
      { label: 'SteamCMD', color: '#90caf9' },
      { label: 'MySQL 8',  color: '#ffb74d' },
    ],
  },
] as const;

// ── Feature timeline view ──────────────────────────────────────────────────

function FeatureTimeline({ items, groupByPhase }: { items: FeatureHistoryEntry[]; groupByPhase: boolean }) {
  const groups = useMemo(() => {
    if (!groupByPhase) return [{ label: null as string | null, items }];
    const map = new Map<string, FeatureHistoryEntry[]>();
    items.forEach((wp) => {
      const key = wp.phase !== null ? `Phase ${wp.phase}` : 'General';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(wp);
    });
    return [...map.entries()]
      .map(([label, its]) => ({ label, items: its }))
      .sort((a, b) => {
        const na = parseInt(a.label?.replace('Phase ', '') ?? '0') || 0;
        const nb = parseInt(b.label?.replace('Phase ', '') ?? '0') || 0;
        return nb - na;
      });
  }, [items, groupByPhase]);

  return (
    <Box>
      {groups.map((group) => (
        <Box key={group.label ?? 'general'} sx={{ mb: 3 }}>
          {groupByPhase && group.label && (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
              <Chip label={group.label} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.07em' }} />
              <Divider sx={{ flex: 1 }} />
              <Typography variant="caption" color="text.secondary">{group.items.length} features</Typography>
            </Stack>
          )}
          <Stack spacing={0.5}>
            {group.items.map((wp) => (
              <Paper key={wp.wp_number} variant="outlined" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                <Chip label={wp.wp_number} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', flexShrink: 0 }} />
                <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>{wp.title}</Typography>
                {wp.phase !== null && (
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>P{wp.phase}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>
                  {wp.solved_at ? new Date(wp.solved_at).toLocaleDateString() : '—'}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export function About() {
  const canManage = usePermission('system.manage');
  const showSnackbar = useSnackbar();
  const [updating, setUpdating] = useState(false);
  const [histSearch, setHistSearch] = useState('');
  const [histPhase, setHistPhase] = useState<number | null | 'all'>('all');
  const [histView, setHistView] = useState<'timeline' | 'table'>('timeline');
  const [groupByPhase, setGroupByPhase] = useState(true);

  const { data: info, isLoading } = useQuery({
    queryKey: ['about'],
    queryFn: fetchAbout,
    staleTime: 60_000,
  });

  const { data: history = [] } = useQuery({
    queryKey: ['about/feature-history'],
    queryFn: fetchFeatureHistory,
    staleTime: 5 * 60_000,
  });

  const { data: wps } = useQuery({
    queryKey: ['dev', 'wps'],
    queryFn: () => listWps(),
    staleTime: 5 * 60_000,
  });

  const handleUpdate = async () => {
    try {
      await triggerUpdate();
      setUpdating(true);
    } catch {
      showSnackbar('Failed to start update', 'error');
    }
  };

  const uptimeSec = info ? Math.floor((Date.now() - new Date(info.started_at).getTime()) / 1000) : null;
  const uptime = uptimeSec !== null
    ? uptimeSec < 3600
      ? `${Math.floor(uptimeSec / 60)} min`
      : `${Math.floor(uptimeSec / 3600)} h ${Math.floor((uptimeSec % 3600) / 60)} min`
    : '—';

  const statsStrip = useMemo(() => ({
    total:         wps?.length ?? 0,
    shipped:       history.length,
    phases:        new Set(history.map((w) => w.phase).filter(Boolean)).size,
    bugs:          wps?.reduce((acc, w) => acc + w.bug_count, 0) ?? 0,
    upcoming:      wps?.filter((w) => w.status === 'upcoming').length ?? 0,
    subtasksDone:  wps?.reduce((acc, w) => acc + (w.subtasks?.filter((s) => s.done).length ?? 0), 0) ?? 0,
  }), [wps, history]);

  const distinctPhases = useMemo(() =>
    [...new Set(history.map((w) => w.phase).filter((p): p is number => p !== null))].sort((a, b) => a - b),
  [history]);

  const filteredHistory = useMemo(() => {
    const q = histSearch.toLowerCase();
    return history.filter((wp) => {
      const matchSearch = !q || wp.wp_number.toLowerCase().includes(q) || wp.title.toLowerCase().includes(q);
      const matchPhase = histPhase === 'all' || (histPhase === null ? wp.phase === null : wp.phase === histPhase);
      return matchSearch && matchPhase;
    });
  }, [history, histSearch, histPhase]);

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>About WebAdmin</Typography>
          <Typography variant="body2" color="text.secondary">
            Version summary, tech stack, and feature history
          </Typography>
        </Box>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<SystemUpdateAltIcon />}
            onClick={() => void handleUpdate()}
            disabled={updating || isLoading}
          >
            Update Application
          </Button>
        )}
      </Stack>

      {/* Version cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        {/* Backend */}
        <Paper sx={{ p: 2.5, flex: 1, borderLeft: '3px solid', borderLeftColor: 'primary.main', position: 'relative' }}>
          <Typography variant="overline" sx={{ color: 'primary.light', fontSize: '0.65rem', letterSpacing: '0.09em' }}>Backend</Typography>
          <Typography variant="h5" fontWeight={700} fontFamily="monospace" sx={{ my: 0.5 }}>
            v{info?.backend_version ?? (isLoading ? '…' : '?')}
          </Typography>
          <Chip
            size="small"
            label="Online"
            icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', boxShadow: '0 0 5px #43a047', ml: 0.5 }} />}
            sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(67,160,71,0.15)', color: 'success.light', border: '1px solid rgba(67,160,71,0.25)', fontSize: '0.7rem' }}
          />
          <Typography variant="body2" color="text.secondary">
            Node.js {info?.node_version ?? '…'} · Express · Socket.io
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={3}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.07em' }}>Uptime</Typography>
              <Typography variant="body2" fontFamily="monospace">{uptime}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.07em' }}>Build</Typography>
              <Typography variant="body2" fontFamily="monospace">{info?.build_date ?? '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.07em' }}>Commit</Typography>
              <Typography variant="body2" fontFamily="monospace" color="text.secondary">{info?.git_commit?.slice(0, 7) ?? '—'}</Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Frontend */}
        <Paper sx={{ p: 2.5, flex: 1, borderLeft: '3px solid', borderLeftColor: 'info.main' }}>
          <Typography variant="overline" sx={{ color: 'info.light', fontSize: '0.65rem', letterSpacing: '0.09em' }}>Frontend</Typography>
          <Typography variant="h5" fontWeight={700} fontFamily="monospace" sx={{ my: 0.5 }}>
            v{frontendPkg.version}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            React {(frontendPkg.dependencies['react'] as string).replace('^', '')} · Vite · MUI v5
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={3}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.07em' }}>Framework</Typography>
              <Typography variant="body2" fontFamily="monospace">React 18 + Vite</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.07em' }}>UI</Typography>
              <Typography variant="body2" fontFamily="monospace">MUI v5</Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Host */}
        <Paper sx={{ p: 2.5, flex: 1, borderLeft: '3px solid', borderLeftColor: 'success.main' }}>
          <Typography variant="overline" sx={{ color: 'success.light', fontSize: '0.65rem', letterSpacing: '0.09em' }}>Host</Typography>
          <Typography variant="h5" fontWeight={700} fontFamily="monospace" sx={{ my: 0.5 }}>tsfx.dustinity.de</Typography>
          <Typography variant="body2" color="text.secondary">Ubuntu 22.04 · nginx · systemd</Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={3}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.07em' }}>OS</Typography>
              <Typography variant="body2" fontFamily="monospace">Ubuntu 22.04</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.07em' }}>Web server</Typography>
              <Typography variant="body2" fontFamily="monospace">nginx</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.07em' }}>Init</Typography>
              <Typography variant="body2" fontFamily="monospace">systemd</Typography>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      {/* Stats strip */}
      <Paper sx={{ display: 'flex', mb: 4, overflow: 'hidden' }}>
        {([
          { label: 'Work Packages',    value: statsStrip.total,        color: 'primary.main'  },
          { label: 'Features Shipped', value: statsStrip.shipped,      color: 'success.main'  },
          { label: 'Phases Completed', value: statsStrip.phases,       color: 'info.main'     },
          { label: 'Bugs Fixed',       value: statsStrip.bugs,         color: 'warning.main'  },
          { label: 'Upcoming',         value: statsStrip.upcoming,     color: 'text.primary'  },
          { label: 'Subtasks Done',    value: statsStrip.subtasksDone, color: '#ba68c8'       },
        ] as const).map((s, i, arr) => (
          <Box key={s.label} sx={{ flex: 1, p: 2, borderRight: i < arr.length - 1 ? 1 : 0, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.65rem', fontWeight: 600, display: 'block' }}>
              {s.label}
            </Typography>
            <Typography variant="h4" fontWeight={700} fontFamily="monospace" sx={{ color: s.color, my: 0.5 }}>
              {s.value}
            </Typography>
          </Box>
        ))}
      </Paper>

      {/* Tech stack */}
      <Typography variant="h6" mb={1.5} fontWeight={700}>Technology Stack</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {STACK_GROUPS.map((group) => (
          <Grid item xs={12} md={4} key={group.label}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: '0.65rem', fontWeight: 600, display: 'block', mb: 1.5 }}>
                {group.label}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {group.items.map((s) => (
                  <Chip
                    key={s.label}
                    label={s.label}
                    size="small"
                    sx={{ bgcolor: s.color + '1a', color: s.color, borderColor: s.color + '4d', border: '1px solid', fontSize: '0.75rem' }}
                  />
                ))}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Feature history */}
      <Typography variant="h6" mb={1.5} fontWeight={700}>Feature History</Typography>

      {/* Toolbar */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search features…"
          value={histSearch}
          onChange={(e) => setHistSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {(['all', null, ...distinctPhases] as const).map((p) => (
            <Chip
              key={String(p)}
              label={p === 'all' ? 'All' : p === null ? 'No phase' : `Phase ${p}`}
              size="small"
              variant={histPhase === p ? 'filled' : 'outlined'}
              color={histPhase === p ? 'primary' : 'default'}
              onClick={() => setHistPhase(p as typeof histPhase)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>
        <Box sx={{ flex: 1 }} />
        {histView === 'timeline' && (
          <Chip
            label={groupByPhase ? 'Group by phase' : 'Flat list'}
            size="small"
            variant={groupByPhase ? 'filled' : 'outlined'}
            color={groupByPhase ? 'primary' : 'default'}
            onClick={() => setGroupByPhase((g) => !g)}
            sx={{ cursor: 'pointer' }}
          />
        )}
        <Chip label={`${filteredHistory.length} shipped`} size="small" variant="outlined" />
        <ToggleButtonGroup size="small" value={histView} exclusive onChange={(_, v: string | null) => v && setHistView(v as 'timeline' | 'table')}>
          <ToggleButton value="timeline">⏱ Timeline</ToggleButton>
          <ToggleButton value="table">⊞ Table</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {histView === 'timeline' ? (
        <FeatureTimeline items={filteredHistory} groupByPhase={groupByPhase} />
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 90, fontWeight: 700 }}>WP</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                <TableCell sx={{ width: 120, fontWeight: 700 }}>Phase</TableCell>
                <TableCell sx={{ width: 130, fontWeight: 700 }}>Resolved</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ color: 'text.secondary', fontStyle: 'italic', py: 2 }}>
                    No resolved work packages yet.
                  </TableCell>
                </TableRow>
              )}
              {filteredHistory.map((wp) => (
                <TableRow key={wp.wp_number} hover>
                  <TableCell>
                    <Chip label={wp.wp_number} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{wp.title}</TableCell>
                  <TableCell sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    {wp.phase !== null ? `Phase ${wp.phase}` : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    {wp.solved_at ? new Date(wp.solved_at).toLocaleDateString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UpdateDialog open={updating} onDone={() => { window.location.reload(); }} />
    </Box>
  );
}
